/**
 * 🔐 JWT local-expiry helper — pure, no network, no react-native.
 *
 * Extracted from AuthContext.tsx's inline cold-start expiry decode so it can be
 * unit-tested AND reused by index.tsx's established-user fast-path
 * (Cold-Start Latency Fix, 2026-05-25). Decoding the base64url payload via the
 * `buffer` package matches AuthContext's prior Hermes-safe decode — `atob` is
 * not a global in React Native.
 *
 * These helpers do NOT verify the JWT signature. They only read claims for the
 * local "is this token worth optimistically trusting?" decision. The server is
 * always the authority on validity (a locally-unexpired token can still be
 * revoked server-side — see index.tsx's background reconcile).
 */

import { Buffer } from 'buffer';

/**
 * Decode a JWT's payload (the middle segment) to a plain object.
 * Returns null for anything we can't confidently parse (≠3 segments,
 * non-base64url middle, non-object / unparseable JSON).
 */
export function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  if (typeof jwt !== 'string') return null;
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = Buffer.from(base64, 'base64').toString('utf-8');
    const payload = JSON.parse(decoded);
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      return payload as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Three-valued local expiry check.
 *
 *   true   — the `exp` claim is in the past. Token is definitely expired;
 *            caller must clear + re-authenticate.
 *   false  — the `exp` claim is in the future. The token is within its
 *            validity window; caller MAY restore/route optimistically without
 *            a network round-trip.
 *   null   — undeterminable: the token is malformed, or has no finite `exp`
 *            claim. Caller must NOT optimistically trust it — fall back to a
 *            server check.
 *
 * Boundary semantics match AuthContext's prior inline check: strict
 * less-than (`exp * 1000 < nowMs`), so a token whose exp lands exactly on
 * `nowMs` is treated as not-yet-expired.
 */
export function isJwtExpired(jwt: string, nowMs: number = Date.now()): boolean | null {
  const payload = decodeJwtPayload(jwt);
  if (!payload) return null;
  const rawExp = payload.exp;
  const exp = typeof rawExp === 'string' ? parseInt(rawExp, 10) : rawExp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
  return exp * 1000 < nowMs;
}
